import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import io, { Socket } from 'socket.io-client';

interface ProductionModeState {
  // Map of lineId to current mode
  lineModes: Record<string, string>;
  // Overall system mode (most common mode across all lines)
  globalMode: string;
  // Timestamp of last update
  lastUpdate: Date | null;
  // WebSocket connection status
  isConnected: boolean;
}

interface ProductionModeContextType extends ProductionModeState {
  // Get mode for specific line or global if no lineId
  getMode: (lineId?: string) => string;
  // Subscribe to mode updates for a specific line
  subscribeTo: (lineId?: string) => void;
  // Unsubscribe from mode updates
  unsubscribeFrom: (lineId?: string) => void;
  // Manually refresh mode from API
  refreshMode: (lineId?: string) => Promise<void>;
}

const ProductionModeContext = createContext<ProductionModeContextType | undefined>(undefined);

export const useProductionMode = () => {
  const context = useContext(ProductionModeContext);
  if (!context) {
    throw new Error('useProductionMode must be used within ProductionModeProvider');
  }
  return context;
};

interface ProductionModeProviderProps {
  children: React.ReactNode;
}

export const ProductionModeProvider: React.FC<ProductionModeProviderProps> = ({ children }) => {
  const [socket, setSocket] = useState<Socket | null>(null);
  const [lineModes, setLineModes] = useState<Record<string, string>>({});
  const [globalMode, setGlobalMode] = useState<string>('stop');
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);
  const [isConnected, setIsConnected] = useState<boolean>(false);

  // Get API URL
  const protocol = window.location.protocol;
  const hostname = window.location.hostname || 'localhost';
  const port = window.location.port ? `:${window.location.port}` : '';
  const apiUrl = `${protocol}//${hostname}${port}/api`;
  const wsUrl = `${protocol.replace('http', 'ws')}//${hostname}${port}`;

  // Initialize WebSocket connection
  useEffect(() => {
    const newSocket = io(wsUrl, {
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: 5
    });

    newSocket.on('connect', () => {
      console.log('ProductionMode WebSocket connected');
      setIsConnected(true);
      // Subscribe to all lines by default
      newSocket.emit('subscribe-production-mode');
    });

    newSocket.on('disconnect', () => {
      console.log('ProductionMode WebSocket disconnected');
      setIsConnected(false);
    });

    // Handle mode change events
    newSocket.on('mode-changed', (data: { lineId: string; mode: string; timestamp: Date }) => {
      console.log('Production mode changed:', data);
      setLineModes(prev => ({
        ...prev,
        [data.lineId]: data.mode
      }));
      setLastUpdate(new Date(data.timestamp));
      
      // Update global mode based on most common mode
      updateGlobalMode();
    });

    setSocket(newSocket);

    // Initial fetch of all line modes
    fetchAllLineModes();

    return () => {
      newSocket.close();
    };
  }, []);

  // Fetch all line modes from API
  const fetchAllLineModes = async () => {
    try {
      const response = await fetch(`${apiUrl}/lines/modes`);
      if (response.ok) {
        const result = await response.json();
        if (result.data) {
          const modes: Record<string, string> = {};
          result.data.forEach((line: { id: string; currentMode: string }) => {
            modes[line.id] = line.currentMode || 'stop';
          });
          console.log('ProductionModeContext: Fetched line modes:', modes);
          setLineModes(modes);
          updateGlobalMode(modes);
          setLastUpdate(new Date());
        }
      }
    } catch (error) {
      console.error('Error fetching line modes:', error);
    }
  };

  // Update global mode based on line modes
  const updateGlobalMode = (modes?: Record<string, string>) => {
    const currentModes = modes || lineModes;
    const modeCount: Record<string, number> = {};
    
    Object.values(currentModes).forEach(mode => {
      modeCount[mode] = (modeCount[mode] || 0) + 1;
    });

    // Find most common mode
    let maxCount = 0;
    let commonMode = 'stop';
    Object.entries(modeCount).forEach(([mode, count]) => {
      if (count > maxCount) {
        maxCount = count;
        commonMode = mode;
      }
    });

    // Priority: if any line is in production, global is production
    if (modeCount['production'] > 0) {
      setGlobalMode('production');
    } else if (modeCount['prepare'] > 0) {
      setGlobalMode('prepare');
    } else if (modeCount['clean'] > 0) {
      setGlobalMode('clean');
    } else {
      setGlobalMode(commonMode);
    }
  };

  // Get mode for specific line or global
  const getMode = useCallback((lineId?: string): string => {
    if (lineId && lineModes[lineId]) {
      return lineModes[lineId];
    }
    return globalMode;
  }, [lineModes, globalMode]);

  // Subscribe to mode updates for a specific line
  const subscribeTo = useCallback((lineId?: string) => {
    if (socket) {
      socket.emit('subscribe-production-mode', lineId);
    }
  }, [socket]);

  // Unsubscribe from mode updates
  const unsubscribeFrom = useCallback((lineId?: string) => {
    if (socket) {
      socket.emit('unsubscribe-production-mode', lineId);
    }
  }, [socket]);

  // Manually refresh mode from API
  const refreshMode = useCallback(async (lineId?: string) => {
    if (lineId) {
      try {
        const response = await fetch(`${apiUrl}/lines/${lineId}/mode`);
        if (response.ok) {
          const result = await response.json();
          if (result.data) {
            setLineModes(prev => ({
              ...prev,
              [lineId]: result.data.current_mode || 'stop'
            }));
            updateGlobalMode();
          }
        }
      } catch (error) {
        console.error('Error refreshing line mode:', error);
      }
    } else {
      await fetchAllLineModes();
    }
  }, [apiUrl]);

  const value: ProductionModeContextType = {
    lineModes,
    globalMode,
    lastUpdate,
    isConnected,
    getMode,
    subscribeTo,
    unsubscribeFrom,
    refreshMode
  };

  return (
    <ProductionModeContext.Provider value={value}>
      {children}
    </ProductionModeContext.Provider>
  );
};

export default ProductionModeContext;